import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, BookOpen, Baby, Calendar, Clock, 
  MessageSquare, Target, Heart, AlertTriangle,
  RefreshCw, ChevronRight, Lightbulb, Star,
  Play, FileText, Quote, TrendingUp, Shield
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverPrepNotes = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [prepNotes, setPrepNotes] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(childId || null);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadChildren();
  }, [navigate]);

  useEffect(() => {
    if (selectedChild) {
      loadPrepNotes(selectedChild);
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      const response = await axios.get(`${BACKEND_URL}/api/observer/dashboard?token=${token}`);
      const childrenData = response.data.children || [];
      setChildren(childrenData);
      if (!selectedChild && childrenData.length > 0) {
        setSelectedChild(childrenData[0].id);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  const loadPrepNotes = async (childId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('observer_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/analysis/observer/prep-notes/${childId}?token=${token}`
      );
      setPrepNotes(response.data);
    } catch (error) {
      console.error('Error loading prep notes:', error);
      setPrepNotes({ has_prep: false, message: "Failed to load prep notes" });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !prepNotes) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="observer-prep-notes">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/observer/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                  Prep Notes
                </h1>
                <p className="text-gray-600">Context and tips for your next call</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => selectedChild && loadPrepNotes(selectedChild)}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Child Selector */}
          {children.length > 0 && (
            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Child</label>
              <div className="flex gap-2 flex-wrap">
                {children.map(child => (
                  <Button
                    key={child.id}
                    variant={selectedChild === child.id ? "default" : "outline"}
                    onClick={() => setSelectedChild(child.id)}
                    className="flex items-center gap-2"
                  >
                    <Baby className="w-4 h-4" />
                    {child.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* No Prep Notes State */}
          {!prepNotes?.has_prep && (
            <Card className="text-center py-12">
              <CardContent>
                <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {prepNotes?.child_name ? `First Call with ${prepNotes.child_name}` : 'No Prep Notes Yet'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {prepNotes?.message || "This will be your first session with this child. Review their profile before the call!"}
                </p>
                <Button onClick={() => navigate(`/observer/child/${selectedChild}`)}>
                  View Child Profile
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Prep Notes Content */}
          {prepNotes?.has_prep && (
            <>
              {/* Child Info Header */}
              <Card className="mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                        <Baby className="w-8 h-8" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{prepNotes.child_name}</h2>
                        <p className="text-blue-100">Age: {prepNotes.child_age} years</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-100 text-sm">Total Sessions</p>
                      <p className="text-3xl font-bold">{prepNotes.total_calls}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Suggested Opening */}
              <Card className="mb-6 border-green-200 bg-green-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <Play className="w-5 h-5" />
                    Suggested Opening
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-white rounded-lg border border-green-100">
                    <p className="text-gray-800 text-lg italic">"{prepNotes.suggested_opening}"</p>
                  </div>
                </CardContent>
              </Card>

              {/* Last Call Summary */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-600" />
                    Last Call Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{prepNotes.last_call_summary}</p>
                </CardContent>
              </Card>

              {/* Two Column Layout */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Follow-up Topics */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Target className="w-5 h-5 text-amber-600" />
                      Topics to Follow Up
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {prepNotes.follow_up_topics?.length > 0 ? (
                      <div className="space-y-2">
                        {prepNotes.follow_up_topics.map((topic, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg">
                            <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-700 text-sm">{topic}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No specific follow-ups needed</p>
                    )}
                  </CardContent>
                </Card>

                {/* Child's Interests */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Heart className="w-5 h-5 text-pink-600" />
                      Child's Interests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {prepNotes.interests?.map((interest, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm"
                        >
                          {interest.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {(!prepNotes.interests || prepNotes.interests.length === 0) && (
                        <span className="text-gray-500 text-sm">Discover interests in your conversations!</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Communication Style */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    Communication Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 font-medium mb-1">Typical Mood</p>
                      <p className="text-gray-800">{prepNotes.typical_mood || 'Varies'}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 font-medium mb-1">Communication Style</p>
                      <p className="text-gray-800 text-sm">{prepNotes.communication_notes}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cautions */}
              {prepNotes.cautions?.length > 0 && (
                <Card className="mb-6 border-red-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <Shield className="w-5 h-5" />
                      Things to Be Mindful Of
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {prepNotes.cautions.map((caution, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700 text-sm">{caution}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <div className="flex gap-4">
                <Button 
                  onClick={() => navigate('/observer/call-analysis')}
                  variant="outline"
                  className="flex-1"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Last Call Analysis
                </Button>
                <Button 
                  onClick={() => navigate(`/observer/child/${selectedChild}`)}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500"
                >
                  <Baby className="w-4 h-4 mr-2" />
                  View Full Profile
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverPrepNotes;
